/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import './Marketplace.scss';
import { Button, Container, Col, Card, Row, Popover, OverlayTrigger } from 'react-bootstrap';
import { connect } from 'react-redux';
import {
  getBotRequestAction,
  getRegisteredBotRequestAction,
  registerRequest
} from '../../store/bot/botActions';
import { setLoadingAction, setLoadingFullAction } from '../../store/loading/loadingActions';
import { useHistory } from 'react-router-dom';
import { accountDetailsRequestAction } from '../../store/account/userActions';
import bnbusdt from '../../assets/img/coin/bnbusdt.png';
import okxusdt from '../../assets/img/coin/okxusdt.png';
import high from '../../assets/img/risk/high.png';
import medium from '../../assets/img/risk/medium.png';
import low from '../../assets/img/risk/low.png';

// Import modern icons (install lucide-react if not already installed)
import { 
  Bot, 
  Box, 
  TrendingUp, 
  Shield, 
  AlertTriangle, 
  Zap, 
  Calendar, 
  User, 
  Link,
  Settings,
  Play,
  Key,
  CheckCircle,
  Info,
  Activity,
  Sparkles
} from 'lucide-react';

function Marketplace(props) {
  const {
    botData,
    setLoadingFull,
    registeredBotData,
    getBotRequest,
    getRegisteredBotRequest,
    user,
    accountDetailsRequest
  } = props;
  const history = useHistory();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setLoadingFull({ isLoading: true, loadingText: 'Loading Strategies...' });
    getBotRequest();
    accountDetailsRequest();
    getRegisteredBotRequest();
  }, [getBotRequest, accountDetailsRequest, getRegisteredBotRequest]);
  
  useEffect(() => {
    if (botData && Object.values(botData).length > 0) {
      setLoading(false);
      setLoadingFull({ isLoading: false, loadingText: '' });
    }
  }, [botData]); 

  const botDataArray = botData ? Object.values(botData) : [];

  const routeStrategie = (e, id, isRegistered = false) => {
    e.stopPropagation();
    const state = isRegistered ? { isEdit: true } : undefined;
    history.push(`/general-settings/bot-management/${id}`, state);
  };

  const redirectApiKey = () => {
    history.push('/general-settings/api-settings');
  };

  const riskPopover = (risk) => (
    <Popover id={`popover-${risk}`} className="modern-popover">
      <Popover.Body>
        {risk === 'low' && 'This bot is designed for low-risk investments, prioritizing stability and safety.'}
        {risk === 'mid' && 'This bot balances risk and reward, executing a moderate number of trades.'}
        {risk === 'high' && 'This bot focuses on aggressive strategies, leveraging market volatility for maximum returns.'}
      </Popover.Body>
    </Popover>
  );

  const isBotRegistered = (botId) => {
    if (!registeredBotData || Object.keys(registeredBotData).length === 0) return false;

    return Object.values(registeredBotData).some(bot =>
      bot.bot_id === botId && bot.registration_status === true
    );
  };

  const getRegisteredBotDetails = (botId) => {
    if (!registeredBotData) return null;

    return Object.values(registeredBotData).find(bot =>
      bot.bot_id === botId && bot.registration_status === true
    );
  };

  const hasAnyApiKey = () => {
    return !!(user?.binance || user?.okx);
  };

  const getAvailableExchanges = () => {
    const exchanges = [];
    if (user?.binance) exchanges.push('binance');
    if (user?.okx) exchanges.push('okx');
    return exchanges;
  };

  const getButtonText = (botItem) => {
    if (!hasAnyApiKey()) {
      return 'Add API Key';
    }

    const registered = isBotRegistered(botItem.id);
    if (registered) {
      const botDetails = getRegisteredBotDetails(botItem.id);
      return `Update Exchange`;
    }

    const userExchanges = getAvailableExchanges();
    const botSupportsUserExchange = botItem.exchanges.some(exchange =>
      userExchanges.includes(exchange.name.toLowerCase())
    );

    if (botSupportsUserExchange) {
      return 'Start Bot';
    } else {
      return 'Add Required API Key';
    }
  };

  // Modern Badge Component
  const ModernBadge = ({ children, variant = 'primary', icon: Icon }) => (
    <span className={`modern-marketplace-badge badge-${variant}`}>
      {Icon && <Icon size={14} />}
      {children}
    </span>
  );

  // Modern Status Indicator
  const StatusIndicator = ({ active }) => (
    <div className={`modern-status-indicator ${active ? 'active' : 'inactive'}`}>
      <div className="status-dot"></div>
      <span className="status-text">{active ? 'Active' : 'Inactive'}</span>
    </div>
  );

  // Risk Level Component
  const RiskLevel = ({ risk }) => {
    const getRiskIcon = () => {
      switch (risk) {
        case 'low': return Shield;
        case 'mid': return AlertTriangle;
        case 'high': return Zap;
        default: return Shield;
      }
    };

    const RiskIcon = getRiskIcon();

    return (
      <div className={`modern-risk-indicator risk-${risk}`}>
        <RiskIcon size={16} />
        <span>{risk.charAt(0).toUpperCase() + risk.slice(1)} Risk</span>
      </div>
    );
  };

  return (
    <div className="modern-marketplace">
      <Container>
        {/* Header Section */}
        <div className="marketplace-header">
          <div className="header-content">
            <div className="header-left">
              <h1 className="marketplace-title">
                <Bot size={32} className="title-icon" />
                Trading Strategies
              </h1>
              <p className="marketplace-subtitle">
                Discover and deploy advanced trading bots powered by AI
              </p>
            </div>
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-number">{botDataArray.length}</span>
                <span className="stat-label">Available Strategies</span>
              </div>
            </div>
          </div>
        </div>

        {/* Strategies Grid */}
        <div className="strategies-grid">
          <Row className="g-4">
            {botDataArray.map((item, index) => {
              const registered = isBotRegistered(item.id);
              const buttonText = getButtonText(item);
              const hasApiKey = hasAnyApiKey();
              const shouldRedirectToApiKey = !hasApiKey ||
                (buttonText === 'Add Required API Key');

              return (
                <Col key={index} sm={12} md={6} lg={4}>
                  <Card className="modern-strategy-card">
                    {/* Card Header */}
                    <div className="strategy-card-header">
                      <div className="strategy-title-section">
                        <h3 className="strategy-name">{item.name}</h3>
                        <StatusIndicator active={item.status} />
                      </div>
                      
                      {registered && (
                        <div className="registered-indicator">
                          <CheckCircle size={20} className="check-icon" />
                          <span>Active</span>
                        </div>
                      )}
                    </div>

                    {/* Strategy Icon and Badges */}
                    <div className="strategy-icon-section">
                      <div className="strategy-icon-wrapper">
                        {item.id === 2 ? (
                          <div className="icon-container ai-bot">
                            <Bot size={48} />
                          </div>
                        ) : (
                          <div className="icon-container regular-bot">
                            <Box size={48} />
                          </div>
                        )}
                      </div>

                      {/* Badges */}
                      <div className="strategy-badges">
                        {item.id === 2 && (
                          <ModernBadge variant="ai" icon={Sparkles}>
                            AI Powered
                          </ModernBadge>
                        )}
                        
                        <RiskLevel risk={item.risk} />

                        {registered && getRegisteredBotDetails(item.id)?.exchange && (
                          <div className="connected-badge">
                            <Link size={14} />
                            <span>
                              {getRegisteredBotDetails(item.id)?.exchange.charAt(0).toUpperCase() + 
                               getRegisteredBotDetails(item.id)?.exchange.slice(1).toLowerCase()}
                            </span>
                            <img
                              src={getRegisteredBotDetails(item.id)?.exchange.toLowerCase() === 'binance' ? bnbusdt : okxusdt}
                              width={20}
                              height={20}
                              alt={getRegisteredBotDetails(item.id)?.exchange}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Strategy Details */}
                    <div className="strategy-details">
                      <div className="detail-item">
                        <User size={16} className="detail-icon" />
                        <span className="detail-label">Creator:</span>
                        <span className="detail-value">{item.creator}</span>
                      </div>

                      <div className="detail-item">
                        <Calendar size={16} className="detail-icon" />
                        <span className="detail-label">Created:</span>
                        <span className="detail-value">
                          {new Date(item.cdate).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="detail-item">
                        <TrendingUp size={16} className="detail-icon" />
                        <span className="detail-label">P&L:</span>
                        <span className={`detail-value pnl-value ${item.pnl_percent >= 0 ? 'positive' : 'negative'}`}>
                          {item.pnl_percent >= 0 ? '+' : ''}{item.pnl_percent.toFixed(2)}%
                        </span>
                      </div>

                      <div className="detail-item risk-detail">
                        <Shield size={16} className="detail-icon" />
                        <span className="detail-label">Risk Level:</span>
                        <div className="risk-info">
                          <span className={`risk-text risk-${item.risk}`}>
                            {item.risk.charAt(0).toUpperCase() + item.risk.slice(1)}
                          </span>
                          <img 
                            src={item.risk === 'low' ? low : item.risk === 'mid' ? medium : high} 
                            alt="risk" 
                            width={20} 
                            height={20} 
                            className="risk-image"
                          />
                          <OverlayTrigger placement="top" overlay={riskPopover(item.risk)}>
                            <Info size={16} className="info-icon" />
                          </OverlayTrigger>
                        </div>
                      </div>

                      <div className="detail-item exchanges-detail">
                        <Activity size={16} className="detail-icon" />
                        <span className="detail-label">Exchanges:</span>
                        <div className="exchanges-list">
                          {item.exchanges.map((exchange, idx) => (
                            <div key={idx} className="exchange-item">
                              <span className="exchange-name">
                                {exchange.name.charAt(0).toUpperCase() + exchange.name.slice(1).toLowerCase()}
                              </span>
                              <img
                                src={exchange.name.toLowerCase() === 'binance' ? bnbusdt : okxusdt}
                                width={20}
                                height={20}
                                alt={exchange.name}
                                className="exchange-logo"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className="strategy-action">
                      <Button
                        onClick={(e) => {
                          if (shouldRedirectToApiKey) {
                            redirectApiKey();
                          } else {
                            routeStrategie(e, item.id, registered);
                          }
                        }}
                        className={`modern-action-btn ${registered ? 'update-btn' : 'start-btn'}`}
                      >
                        {!hasApiKey ? (
                          <>
                            <Key size={16} />
                            <span>Add API Key</span>
                          </>
                        ) : buttonText === 'Add Required API Key' ? (
                          <>
                            <Key size={16} />
                            <span>Add Required API Key</span>
                          </>
                        ) : registered ? (
                          <>
                            <Settings size={16} />
                            <span>Update Exchange</span>
                          </>
                        ) : (
                          <>
                            <Play size={16} />
                            <span>Start Bot</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>

        {/* Empty State */}
        {botDataArray.length === 0 && !loading && (
          <div className="empty-state">
            <Bot size={64} className="empty-icon" />
            <h3>No Strategies Available</h3>
            <p>Check back later for new trading strategies</p>
          </div>
        )}
      </Container>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    botData: state.botReducer.botData,
    registeredBotData: state.botReducer.registeredBotData,
    user: state.userReducer.user,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getBotRequest: (payload) => {
      dispatch(getBotRequestAction(payload));
    },
    getRegisteredBotRequest: (payload) => {
      dispatch(getRegisteredBotRequestAction(payload));
    },
    setLoading: (payload) => {
      dispatch(setLoadingAction(payload));
    },
    setLoadingFull: (payload) => {
      dispatch(setLoadingFullAction(payload));
    },
    registerRequest: (creds) => {
      dispatch(registerRequest(creds));
    },
    accountDetailsRequest: (payload) => {
      dispatch(accountDetailsRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Marketplace);